// AppShell — the responsive layout (variant C: rail + sub-nav + content).
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Rail } from "./Rail";
import { SubNav } from "./SubNav";
import { TopBar } from "./TopBar";
import { TabBar } from "./TabBar";
import { useTabs } from "./tabsStore";
import { moduleByPath } from "./modules";
import { CmdK } from "./CmdK";
import { Toasts } from "./Toasts";
import { Tweaks } from "./Tweaks";
import { RealtimeNotifier } from "./RealtimeNotifier";
import { NotifPanel } from "./NotifPanel";
import { AiPanel } from "./AiPanel";
import { useAuth } from "../shared/stores/auth";
import { authApi } from "../shared/api/auth";
import { api } from "../shared/api/client";

function readNum(key: string, fallback: number): number {
  const v = localStorage.getItem(key);
  if (!v) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function readBool(key: string, fallback: boolean): boolean {
  const v = localStorage.getItem(key);
  return v == null ? fallback : v === "1";
}

export function AppShell() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, setManifest, setUser } = useAuth();

  // Layout state
  const [railWidth, setRailWidth] = useState(() => readNum("t-rail-w", 120));
  const [railMin, setRailMin] = useState(() => readBool("t-rail-min", false));
  const [subWidth, setSubWidth] = useState(() => readNum("t-sub-w", 220));
  const [subCollapsed, setSubCollapsed] = useState(() => readBool("t-sub-c", false));
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  // Multi-screen tabs — only in the installed "program" (standalone) mode, where
  // there's no browser tab strip. Force on with localStorage t-tabs-force=1 for testing.
  const [showTabs] = useState(() => {
    try {
      if (localStorage.getItem("t-tabs-force") === "1") return true;
      if (/electron/i.test(navigator.userAgent)) return true; // 데스크톱(.exe) 프로그램
      return !!window.matchMedia?.("(display-mode: standalone)")?.matches
        || (navigator as any).standalone === true;
    } catch { return false; }
  });
  const syncActive = useTabs((s) => s.syncActive);
  useEffect(() => {
    if (!showTabs) return;
    const mod = moduleByPath(loc.pathname);
    const seg = loc.pathname.split("/").filter(Boolean)[0] ?? "home";
    syncActive({
      url: loc.pathname + loc.search,
      title: mod?.label ?? (seg.charAt(0).toUpperCase() + seg.slice(1)),
      icon: (mod?.icon ?? "Home") as any,
      color: mod?.color ?? "var(--p-500)",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, loc.search, showTabs]);
  const [aiOpen, setAiOpen] = useState(() => readBool("t-ai-open", false));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Breakpoint
  const [bp, setBp] = useState<"mobile" | "tablet" | "desktop">(() => readBp());
  useEffect(() => {
    const f = () => setBp(readBp());
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);

  // Close the mobile drawer whenever the route (path or query) changes — so
  // tapping a SubNav folder navigates and dismisses the drawer.
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname, loc.search]);

  // Presence heartbeat — while the app is open, ping every 60s so the member
  // list can show 접속중(초록)/1시간 이내(노랑)/미접속(회색). Also ping on tab focus.
  useEffect(() => {
    if (!user) return;
    const ping = () => { api.post("/chat/presence/ping").catch(() => {}); };
    ping();
    const iv = window.setInterval(ping, 60_000);
    const onVis = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", ping);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", ping);
    };
  }, [user]);

  // Cmd+K hotkey
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen((x) => !x);
      }
    };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, []);

  // Persist
  useEffect(() => { localStorage.setItem("t-rail-w", String(railWidth)); }, [railWidth]);
  useEffect(() => { localStorage.setItem("t-rail-min", railMin ? "1" : "0"); }, [railMin]);
  useEffect(() => { localStorage.setItem("t-sub-w", String(subWidth)); }, [subWidth]);
  useEffect(() => { localStorage.setItem("t-sub-c", subCollapsed ? "1" : "0"); }, [subCollapsed]);
  useEffect(() => { localStorage.setItem("t-ai-open", aiOpen ? "1" : "0"); }, [aiOpen]);

  // Always refresh the permission manifest on mount so menu/permission changes
  // (e.g. via the admin "메뉴 권한 관리" page) propagate without a re-login.
  // A cached manifest still renders immediately; this just keeps it current.
  useEffect(() => {
    authApi.manifest().then(setManifest).catch(() => { nav("/login"); });
    if (!user) {
      authApi.me().then(setUser).catch(() => { /* manifest call will redirect */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "크게 보기" — opened in a new window with ?bare=1, render only the page
  // content (no rail / sub-nav / top bar) so a single sub-page fills the screen.
  //
  // `?app=1` 은 모바일 앱 껍데기 안에서 열렸다는 뜻이다. 화면이 좁은 데다 그 프로그램
  // 하나만 쓰려고 들어온 것이므로 사이트 틀을 통째로 걷어낸다 — bare 와 같게 다룬다.
  const shellParams = new URLSearchParams(loc.search);
  const bare = shellParams.get("bare") === "1" || shellParams.get("app") === "1";
  if (bare) {
    return (
      <div className="timple-root">
        <main className="t-main" style={{ height: "100%" }}>
          <Outlet />
        </main>
        <Toasts />
        <Tweaks />
      </div>
    );
  }

  return (
    <div className="timple-root">
      {showTabs && <TabBar />}
      <TopBar
        isMobile={bp === "mobile"}
        onSearch={() => setCmdkOpen(true)}
        onMenu={() => setDrawerOpen(true)}
        onNotifClick={() => setNotifOpen((x) => !x)}
        onAiClick={() => setAiOpen((x) => !x)}
        aiOpen={aiOpen}
      />

      <div style={{ display: "flex", height: showTabs ? "calc(100% - var(--top) - 38px)" : "calc(100% - var(--top))" }}>
        {bp !== "mobile" && (
          <>
            <Rail
              width={railWidth}
              minimized={railMin || bp === "tablet"}
              onResize={setRailWidth}
              onMinimize={setRailMin}
            />
            {/* SubNav (서브 메뉴) now shows on tablet too, not desktop-only */}
            <SubNav
              width={subWidth}
              onResize={setSubWidth}
              collapsed={subCollapsed}
              onCollapse={setSubCollapsed}
            />
          </>
        )}

        <main className="t-main">
          <Outlet />
        </main>

        {/* AI panel — pushes content from the right on desktop */}
        {aiOpen && bp === "desktop" && (
          <AiPanel onClose={() => setAiOpen(false)} />
        )}
      </div>

      {/* AI panel overlay on mobile/tablet */}
      {aiOpen && bp !== "desktop" && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.4)" }}
            onClick={() => setAiOpen(false)}
          />
          <div style={{ position: "fixed", top: "var(--top)", right: 0, bottom: 0, zIndex: 61, display: "flex" }}>
            <AiPanel onClose={() => setAiOpen(false)} />
          </div>
        </>
      )}

      {bp === "mobile" && drawerOpen && (
        <>
          <div className="t-drawer-back" onClick={() => setDrawerOpen(false)} />
          <div className="t-drawer">
            <div className="t-drawer-h">
              <img src="/timple-logo.jpg" alt="TIMPLE" style={{ height: 22 }}/>
              <button className="t-iconbtn" onClick={() => setDrawerOpen(false)}><span style={{ fontSize: 20 }}>×</span></button>
            </div>
            <div className="t-drawer-nav" style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
              <Rail width={240} minimized={false} onResize={() => {}} onMinimize={() => {}} />
              {/* 현재 모듈의 서브 메뉴(폴더트리)도 드로어에 표시 */}
              <SubNav width={240} onResize={() => {}} collapsed={false} onCollapse={() => {}} />
            </div>
          </div>
        </>
      )}

      <CmdK open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
      {notifOpen && <NotifPanel onClose={() => setNotifOpen(false)} />}
      <Toasts />
      <RealtimeNotifier />
      <Tweaks />
    </div>
  );
}

function readBp(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 720) return "mobile";
  if (w < 1180) return "tablet";
  return "desktop";
}
